import React from 'react';
import {
    Container,
    Box,
    Typography,
    Button,
    Paper,
    Stack
} from '@mui/material';
import SEOHead from '../../components/SEOHead';
import SiteHeader from '../../components/SiteHeader';
import AppFooter from '../../components/AppFooter';
import { useNavigate } from 'react-router-dom';

const LIGHT_PURPLE = '#A76FFF';

const WebAboutUsScreen: React.FC = () => {
    const navigate = useNavigate();

    return (
        <>
            <SEOHead
                title="Our Story | Pop The Balloon Dating App"
                description="Discover how William Mays created Pop The Balloon after being captivated by 'Pop the Balloon or Find Love' YouTube videos. A revolutionary dating app built for authentic connections, real reactions, and genuine chemistry."
                canonical="https://www.poptheballoonapp.com/about-us"
                ogTitle="Our Story - From YouTube Show to Revolutionary Dating App"
                ogDescription="From watching Pop the Balloon or Find Love clips to creating a revolutionary dating app. Learn how William Mays turned entertainment into authentic connections."
                ogUrl="https://www.poptheballoonapp.com/about-us"
            />
            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(180deg, #FCFCFF 0%, #F1E4FF 100%)',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Header */}
                <SiteHeader />

                <Container maxWidth="lg" sx={{ flexGrow: 1, py: 2, pt: { xs: 4, md: 6 } }}>
                    {/* Content */}
                    <Box sx={{ py: { xs: 2, md: 4 } }}>
                        {/* Hero Section */}
                        <Container maxWidth="lg" sx={{ mb: { xs: 6, md: 10 } }}>
                            <Box sx={{ textAlign: 'center', maxWidth: '900px', mx: 'auto' }}>
                                <Typography
                                    variant="h2"
                                    component="h1"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 3,
                                        fontSize: { xs: '2.5rem', md: '3.5rem' },
                                        lineHeight: 1.2,
                                        color: '#1a1a1a'
                                    }}
                                >
                                    From YouTube Rabbit Hole to Revolutionary Dating App
                                </Typography>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        color: 'text.secondary',
                                        fontWeight: 400,
                                        fontSize: { xs: '1.1rem', md: '1.35rem' },
                                        lineHeight: 1.6,
                                        maxWidth: '700px',
                                        mx: 'auto'
                                    }}
                                >
                                    The story of how one person's obsession with a dating show became an app that's changing how people find authentic connections.
                                </Typography>
                            </Box>
                        </Container>

                        {/* Founder's Story Section */}
                        <Container maxWidth="md" sx={{ mb: { xs: 8, md: 12 } }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 4, md: 6 },
                                    borderRadius: 4,
                                    bgcolor: 'white',
                                    border: '1px solid',
                                    borderColor: 'rgba(0,0,0,0.08)'
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: { xs: '1.125rem', md: '1.25rem' },
                                        lineHeight: 1.8,
                                        color: '#2a2a2a',
                                        mb: 3
                                    }}
                                >
                                    It all started with a YouTube rabbit hole. I'm <strong>William Mays</strong>, and like millions of others,
                                    I found myself completely hooked on watching "Pop the Balloon or Find Love" clips.
                                    There was something incredibly entertaining about seeing people make real, honest decisions
                                    in the moment, no scripts, no filters, just genuine human reactions.
                                </Typography>

                                {/* Pull Quote */}
                                <Box
                                    sx={{
                                        my: 5,
                                        py: 4,
                                        px: { xs: 3, md: 5 },
                                        borderLeft: `4px solid ${LIGHT_PURPLE}`,
                                        bgcolor: 'rgba(167, 111, 255, 0.05)',
                                        borderRadius: '0 8px 8px 0'
                                    }}
                                >
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontStyle: 'italic',
                                            fontWeight: 500,
                                            color: '#1a1a1a',
                                            fontSize: { xs: '1.25rem', md: '1.5rem' },
                                            lineHeight: 1.5
                                        }}
                                    >
                                        "Why doesn't this exist as an app?"
                                    </Typography>
                                    <Typography
                                        sx={{
                                            mt: 2,
                                            fontSize: '1rem',
                                            color: 'text.secondary'
                                        }}
                                    >
                                        The question that started it all
                                    </Typography>
                                </Box>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: { xs: '1.125rem', md: '1.25rem' },
                                        lineHeight: 1.8,
                                        color: '#2a2a2a',
                                        mb: 3
                                    }}
                                >
                                    As I watched episode after episode, that thought kept nagging at me.
                                    Traditional dating apps had become exhausting, endless swiping, ghosting, and conversations that went nowhere.
                                    But this show format? It was everything dating apps weren't: fun, authentic, and exciting.
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: { xs: '1.125rem', md: '1.25rem' },
                                        lineHeight: 1.8,
                                        color: '#2a2a2a'
                                    }}
                                >
                                    That's when I decided to turn inspiration into reality. I set out to create an app that captures
                                    the same energy and honesty that made those shows so addictive, while making it accessible to everyone
                                    looking for genuine connections.
                                </Typography>
                            </Paper>
                        </Container>

                        {/* Timeline Section */}
                        <Container maxWidth="md" sx={{ mb: { xs: 8, md: 12 } }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: 700,
                                    mb: 6,
                                    textAlign: 'center',
                                    fontSize: { xs: '2rem', md: '2.5rem' }
                                }}
                            >
                                The Journey
                            </Typography>

                            <Stack spacing={4}>
                                {/* Milestone 1 */}
                                <Box sx={{ display: 'flex', gap: 3 }}>
                                    <Box
                                        sx={{
                                            minWidth: 60,
                                            height: 60,
                                            borderRadius: '50%',
                                            bgcolor: LIGHT_PURPLE,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontWeight: 700,
                                            fontSize: '1.25rem',
                                            boxShadow: '0 4px 12px rgba(167, 111, 255, 0.3)'
                                        }}
                                    >
                                        1
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                            The Inspiration
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                            Discovered "Pop the Balloon or Find Love" on YouTube and became captivated by the authentic,
                                            unscripted interactions between contestants.
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Milestone 2 */}
                                <Box sx={{ display: 'flex', gap: 3 }}>
                                    <Box
                                        sx={{
                                            minWidth: 60,
                                            height: 60,
                                            borderRadius: '50%',
                                            bgcolor: LIGHT_PURPLE,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontWeight: 700,
                                            fontSize: '1.25rem',
                                            boxShadow: '0 4px 12px rgba(167, 111, 255, 0.3)'
                                        }}
                                    >
                                        2
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                            The Vision
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                            Realized the dating app market needed something different — an experience that prioritized
                                            genuine reactions over superficial swiping.
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Milestone 3 */}
                                <Box sx={{ display: 'flex', gap: 3 }}>
                                    <Box
                                        sx={{
                                            minWidth: 60,
                                            height: 60,
                                            borderRadius: '50%',
                                            bgcolor: LIGHT_PURPLE,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontWeight: 700,
                                            fontSize: '1.25rem',
                                            boxShadow: '0 4px 12px rgba(167, 111, 255, 0.3)'
                                        }}
                                    >
                                        3
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                            The Creation
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                            Built Pop The Balloon from the ground up to bring the excitement of the show format
                                            to your phone, making authentic dating accessible to everyone.
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Milestone 4 */}
                                <Box sx={{ display: 'flex', gap: 3 }}>
                                    <Box
                                        sx={{
                                            minWidth: 60,
                                            height: 60,
                                            borderRadius: '50%',
                                            bgcolor: LIGHT_PURPLE,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontWeight: 700,
                                            fontSize: '1.25rem',
                                            boxShadow: '0 4px 12px rgba(167, 111, 255, 0.3)'
                                        }}
                                    >
                                        4
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                            Today
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                            Pop The Balloon is live on Android, with a free beta on iPhone, helping thousands find real connections
                                            through honest reactions and genuine chemistry. One balloon at a time.
                                        </Typography>
                                    </Box>
                                </Box>
                            </Stack>
                        </Container>

                        {/* Mission Statement */}
                        <Container maxWidth="md" sx={{ mb: { xs: 8, md: 12 } }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 4, md: 6 },
                                    textAlign: 'center',
                                    borderRadius: 4,
                                    bgcolor: 'linear-gradient(135deg, rgba(167, 111, 255, 0.1), rgba(255, 90, 118, 0.1))',
                                    background: 'linear-gradient(135deg, rgba(167, 111, 255, 0.1), rgba(255, 90, 118, 0.1))',
                                    border: '1px solid',
                                    borderColor: 'rgba(167, 111, 255, 0.2)'
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 3,
                                        fontSize: { xs: '1.75rem', md: '2rem' }
                                    }}
                                >
                                    Our Mission
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 400,
                                        lineHeight: 1.8,
                                        color: 'text.secondary',
                                        maxWidth: '700px',
                                        mx: 'auto',
                                        fontSize: { xs: '1.1rem', md: '1.25rem' }
                                    }}
                                >
                                    To bring authentic connections back to dating. No endless swiping. No fake personas.
                                    Just real people making honest decisions, experiencing genuine chemistry, and finding meaningful relationships.
                                </Typography>
                            </Paper>
                        </Container>

                        {/* Contact CTA */}
                        <Container maxWidth="sm">
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        mb: 2,
                                        fontWeight: 600,
                                        fontSize: { xs: '1.5rem', md: '1.75rem' }
                                    }}
                                >
                                    Questions or Feedback?
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        mb: 3,
                                        fontSize: { xs: '1rem', md: '1.125rem' },
                                        color: 'text.secondary',
                                        lineHeight: 1.7
                                    }}
                                >
                                    We'd love to hear from you. Reach out to our team at
                                </Typography>
                                <Button
                                    variant="contained"
                                    href="mailto:hello@poptheballoonapp.com"
                                    sx={{
                                        bgcolor: LIGHT_PURPLE,
                                        color: 'white',
                                        px: 4,
                                        py: 1.5,
                                        fontSize: '1.1rem',
                                        fontWeight: 600,
                                        borderRadius: 3,
                                        textTransform: 'none',
                                        boxShadow: '0 4px 14px rgba(167, 111, 255, 0.4)',
                                        '&:hover': {
                                            bgcolor: '#8A5CF5',
                                            boxShadow: '0 6px 20px rgba(167, 111, 255, 0.5)',
                                            transform: 'translateY(-2px)'
                                        },
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    hello@poptheballoonapp.com
                                </Button>
                            </Box>
                        </Container>
                    </Box>
                </Container>

                {/* Footer */}
                <AppFooter />
            </Box>
        </>
    );
};

export default WebAboutUsScreen; 